import { createInstance } from "i18next";
import { useSyncExternalStore } from "react";

import shellEn from "./catalog/shell.en.json";
import shellEl from "./catalog/shell.el.json";
import coreEn from "./catalog/core.en.json";
import coreEl from "./catalog/core.el.json";
import operationsEn from "./catalog/operations.en.json";
import operationsEl from "./catalog/operations.el.json";
import errorsEn from "./catalog/errors.en.json";
import errorsEl from "./catalog/errors.el.json";
import financeEn from "./catalog/finance.en.json";
import financeEl from "./catalog/finance.el.json";

import agentTemplateEn from "./catalog/templates.en.json";
import agentTemplateEl from "./catalog/templates.el.json";

import messagingMediaEn from "./catalog/channels.en.json";
import messagingMediaEl from "./catalog/channels.el.json";

// plan-019 REG: the help assistant's portal phrases (WAPP-HOST fills both; empty until then). Its own catalog, never
// shell.*.json (plan-019 ED-26).
import helpEn from "./catalog/help.en.json";
import helpEl from "./catalog/help.el.json";

// plan-020 REG: the plan screens' phrases under /finance/plans (WAPP-PLAN; file plan.*.json, namespace `subscription`,
// because "plan" is customer copy and never an identifier, glossary §6.35) and the Billing Plan section and banner's
// (WAPP-BILL; namespace `billing`) — each empty until its unit lands.
import subscriptionEn from "./catalog/plan.en.json";
import subscriptionEl from "./catalog/plan.el.json";
import billingEn from "./catalog/billing.en.json";
import billingEl from "./catalog/billing.el.json";

// plan-021 REG: the VoiceAgent pass's phrases — the Channels tab's pass panel and the lifecycle referrers (WAPP-PASS;
// namespace `voiceAgentPass`) and History, Live, Analytics and the test console with passes (WAPP-HIST; namespace
// `voiceAgentPassHistory`) — each empty until its unit lands, so neither unit edits this file.
import voiceAgentPassEn from "./catalog/voice-agent-pass.en.json";
import voiceAgentPassEl from "./catalog/voice-agent-pass.el.json";
import voiceAgentPassHistoryEn from "./catalog/voice-agent-pass-history.en.json";
import voiceAgentPassHistoryEl from "./catalog/voice-agent-pass-history.el.json";

// plan-029 REG: caller protection — the Channels tab's caller-protection panel, blocked numbers, the access list, History
// reasons and the notices (WAPP-PROT, WAPP-ACCESS, WAPP-HIST1; namespace `callerProtection`); caller confirmation
// (WAPP-CONF, WAPP-HIST2; namespace `partyConfirmation`); the signed visitor's set-up (WAPP-VIS; namespace
// `signedVisitor`) — each empty until its unit lands, so no plan-029 unit edits this file.
import callerProtectionEn from "./catalog/caller-protection.en.json";
import callerProtectionEl from "./catalog/caller-protection.el.json";
import partyConfirmationEn from "./catalog/party-confirmation.en.json";
import partyConfirmationEl from "./catalog/party-confirmation.el.json";
import signedVisitorEn from "./catalog/signed-visitor.en.json";
import signedVisitorEl from "./catalog/signed-visitor.el.json";

// plan-030 WAPP-Q: the survey question editor — the Questions tab, its problems and warnings (namespace
// `surveyQuestionnaire`, design §41.6.3, §41.10).
import surveyQuestionnaireEn from "./catalog/survey-questionnaire.en.json";
import surveyQuestionnaireEl from "./catalog/survey-questionnaire.el.json";

// plan-034 WAPP-APPSIGNIN: the wetalk app's consent page `/app-sign-in` and the static return page
// `/app-link/sign-in/finish` (namespace `appSignIn`, design §45.4).
import appSignInEn from "./catalog/app-sign-in.en.json";
import appSignInEl from "./catalog/app-sign-in.el.json";

export const PORTAL_LOCALE = ["en", "el"] as const;
export type PortalLocale = (typeof PORTAL_LOCALE)[number];
export type PortalValues = Readonly<Record<string, string | number>>;
export const portalResources = {
  en: { messagingMedia: messagingMediaEn, agentTemplate: agentTemplateEn, shell: shellEn, core: coreEn, operations: operationsEn, finance: financeEn, errors: errorsEn, help: helpEn, subscription: subscriptionEn, billing: billingEn, voiceAgentPass: voiceAgentPassEn, voiceAgentPassHistory: voiceAgentPassHistoryEn, callerProtection: callerProtectionEn, partyConfirmation: partyConfirmationEn, signedVisitor: signedVisitorEn, surveyQuestionnaire: surveyQuestionnaireEn, appSignIn: appSignInEn },
  el: { messagingMedia: messagingMediaEl, agentTemplate: agentTemplateEl, shell: shellEl, core: coreEl, operations: operationsEl, finance: financeEl, errors: errorsEl, help: helpEl, subscription: subscriptionEl, billing: billingEl, voiceAgentPass: voiceAgentPassEl, voiceAgentPassHistory: voiceAgentPassHistoryEl, callerProtection: callerProtectionEl, partyConfirmation: partyConfirmationEl, signedVisitor: signedVisitorEl, surveyQuestionnaire: surveyQuestionnaireEl, appSignIn: appSignInEl },
} as const;

export type PortalNamespace = keyof typeof portalResources.en;
type PluralBase<Key> = Key extends `${infer Base}_one` | `${infer Base}_other` ? Base : never;
export type PortalKey<Namespace extends PortalNamespace> = Extract<keyof typeof portalResources.en[Namespace], string> | PluralBase<keyof typeof portalResources.en[Namespace]>;

const instance = createInstance();
void instance.init({
  resources: portalResources,
  lng: "en",
  supportedLngs: [...PORTAL_LOCALE],
  fallbackLng: false,
  initAsync: false,
  keySeparator: false,
  interpolation: { escapeValue: false },
  returnNull: false,
});

export function portalLocale(): PortalLocale {
  return instance.language === "el" ? "el" : "en";
}

export function portalLocaleOf(value: string | null | undefined): PortalLocale | null {
  const base = value?.toLowerCase().split(/[-_]/)[0];
  return base === "en" || base === "el" ? base : null;
}

export async function setPortalLocale(locale: PortalLocale): Promise<void> {
  await instance.changeLanguage(locale);
}

function subscribe(listener: () => void): () => void {
  instance.on("languageChanged", listener);
  return () => { instance.off("languageChanged", listener); };
}

/** Used by copy helpers at call time; React components subscribe with the hook below. */
export function portalText<Namespace extends PortalNamespace>(namespace: Namespace, key: PortalKey<Namespace>, values?: PortalValues): string {
  if (!instance.exists(key, { ns: namespace, ...values })) {
    throw new Error(`Missing portal phrase ${namespace}:${key} in ${portalLocale()}.`);
  }
  return instance.t(key, { ns: namespace, ...values });
}

export function usePortalTranslation<Namespace extends PortalNamespace>(namespace: Namespace): {
  readonly locale: PortalLocale;
  readonly t: (key: PortalKey<Namespace>, values?: PortalValues) => string;
} {
  const locale = useSyncExternalStore(subscribe, portalLocale, () => "en" as const);
  return { locale, t: (key, values) => portalText(namespace, key, values) };
}

/** Display formatting only: callers retain the domain's currency and time-zone rules. */
export function portalFormat(locale: PortalLocale) {
  const language = locale === "el" ? "el-GR" : "en-GB";
  return {
    number: (value: number | bigint, options?: Intl.NumberFormatOptions): string =>
      new Intl.NumberFormat(language, options).format(value),
    date: (value: Date | string, options?: Intl.DateTimeFormatOptions): string =>
      new Intl.DateTimeFormat(language, options).format(typeof value === "string" ? new Date(value) : value),
    relative: (value: number, unit: Intl.RelativeTimeFormatUnit): string =>
      new Intl.RelativeTimeFormat(language, { numeric: "auto" }).format(value, unit),
  };
}
